import type { PageObservation, MutationInfo } from '../types/observations';
import { ElementSelector } from './element-selector';
import { FormAnalyzer } from './form-analyzer';
import { TextContentAnalyzer } from './text-content-analyzer';
import { MutationWatcher } from './mutation-watcher';
import { getViewportInfo } from '../utils/dom-utils';
import { EventBus } from '../core/event-bus';
import { Logger } from '../core/logger';

export class DOMObserver {
  private elementSelector: ElementSelector;
  private formAnalyzer: FormAnalyzer;
  private textContentAnalyzer: TextContentAnalyzer;
  private mutationWatcher: MutationWatcher;
  private eventBus: EventBus;
  private logger: Logger;
  private autoObserve: boolean;
  private observeInterval: number;
  private observeTimer: number | null = null;

  constructor(
    eventBus: EventBus,
    logger: Logger,
    autoObserve = true,
    observeInterval = 1000
  ) {
    this.eventBus = eventBus;
    this.logger = logger;
    this.autoObserve = autoObserve;
    this.observeInterval = observeInterval;

    this.elementSelector = new ElementSelector();
    this.formAnalyzer = new FormAnalyzer();
    this.textContentAnalyzer = new TextContentAnalyzer();
    this.mutationWatcher = new MutationWatcher(logger);
  }

  /**
   * Start observing the page
   */
  start(): void {
    this.logger.info('Starting DOM observer');

    // Start mutation watcher
    this.mutationWatcher.start();
    this.mutationWatcher.on((mutations: MutationInfo[]) => {
      this.handleMutations(mutations);
    });

    // Start periodic observation if auto-observe is enabled
    if (this.autoObserve) {
      this.startAutoObserve();
    }

    this.eventBus.emit('observer:started');
  }

  /**
   * Stop observing the page
   */
  stop(): void {
    this.logger.info('Stopping DOM observer');

    this.mutationWatcher.stop();
    this.stopAutoObserve();

    this.eventBus.emit('observer:stopped');
  }

  /**
   * Observe the current page state
   */
  observe(): PageObservation {
    this.logger.debug('Observing page');

    const observation: PageObservation = {
      url: window.location.href,
      title: document.title,
      viewport: getViewportInfo(),
      scroll: this.textContentAnalyzer.getScrollInfo(),
      contentSize: this.textContentAnalyzer.getContentSize(),
      elements: this.elementSelector.getInteractiveElements(),
      forms: this.formAnalyzer.analyzeForms(),
      textContent: this.textContentAnalyzer.getTextContent(),
      headings: this.textContentAnalyzer.getHeadings(),
      alerts: this.textContentAnalyzer.getAlerts(),
      timestamp: Date.now()
    };

    this.eventBus.emit('observation:complete', observation);
    this.logger.debug('Page observed', {
      elements: observation.elements.length,
      forms: observation.forms.length,
      textContent: observation.textContent?.length || 0,
      headings: observation.headings?.length || 0,
      alerts: observation.alerts?.length || 0
    });

    return observation;
  }

  /**
   * Get current page observation
   */
  getObservation(): PageObservation {
    return this.observe();
  }

  /**
   * Start automatic periodic observation
   */
  private startAutoObserve(): void {
    if (this.observeTimer) {
      return;
    }

    this.logger.debug(`Starting auto-observe (interval: ${this.observeInterval}ms)`);

    this.observeTimer = window.setInterval(() => {
      const observation = this.observe();
      this.eventBus.emit('observation:auto', observation);
    }, this.observeInterval);
  }

  /**
   * Stop automatic observation
   */
  private stopAutoObserve(): void {
    if (this.observeTimer) {
      clearInterval(this.observeTimer);
      this.observeTimer = null;
    }
  }

  /**
   * Handle DOM mutations
   */
  private handleMutations(mutations: MutationInfo[]): void {
    this.logger.debug('DOM mutations detected', { count: mutations.length });
    this.eventBus.emit('dom:mutated', mutations);

    // Trigger a new observation if significant changes detected
    if (this.isSignificantMutation(mutations)) {
      this.logger.debug('Significant mutation detected, triggering observation');
      const observation = this.observe();
      this.eventBus.emit('observation:mutation', observation);
    }
  }

  /**
   * Check if mutations are significant enough to trigger observation
   */
  private isSignificantMutation(mutations: MutationInfo[]): boolean {
    // Consider mutations significant if there are multiple changes
    // or if they involve added/removed elements
    const addedOrRemoved = mutations.filter(
      m => m.type === 'added' || m.type === 'removed'
    );

    return addedOrRemoved.length > 0;
  }

  /**
   * Enable/disable auto-observe
   */
  setAutoObserve(enabled: boolean): void {
    this.autoObserve = enabled;

    if (enabled) {
      this.startAutoObserve();
    } else {
      this.stopAutoObserve();
    }
  }

  /**
   * Set observation interval
   */
  setObserveInterval(interval: number): void {
    this.observeInterval = interval;

    if (this.observeTimer) {
      this.stopAutoObserve();
      this.startAutoObserve();
    }
  }

  /**
   * Get element selector instance
   */
  getElementSelector(): ElementSelector {
    return this.elementSelector;
  }

  /**
   * Get form analyzer instance
   */
  getFormAnalyzer(): FormAnalyzer {
    return this.formAnalyzer;
  }
}
